// FLAGS: none
// One session on the expert's sign-up list: sign up, withdraw the offer, or (once picked as host or cohost) withdraw
// as host while sign-ups are open.
import { useTranslation } from "react-i18next";
import Button from "@openup/shared/components/Button/Button";
import { ResponseConveneHostableSessionResponse } from "@src/api/v1/data-contracts";
import WithdrawAsHostButton from "@src/views/HostSignups/WithdrawAsHostButton";
import { formatStandardDate, formatTimeOnly } from "@src/utils/formatter";

interface HostSignupRowProps {
  session: ResponseConveneHostableSessionResponse;
  busy: boolean;
  /** The session's type as a pill, e.g. "Group session"; left out when the list is already filtered by type. */
  typeLabel?: string;
  onSignUp: () => void;
  onWithdraw: () => void;
}

const HostSignupRow = ({
  session,
  busy,
  typeLabel = undefined,
  onSignUp,
  onWithdraw,
}: HostSignupRowProps) => {
  const { t } = useTranslation();
  const substitute = session.placementState === "substitute-needed";
  // Picked as host or cohost: no longer a candidate, so the offer can't be withdrawn, only the seat.
  const seated = session.myRole === "host" || session.myRole === "cohost";

  return (
    <li
      className="flex flex-wrap items-center justify-between gap-3 py-3"
      data-testid="host-signup-row"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-indigo-800">{session.title}</span>
          {typeLabel && (
            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-800">
              {typeLabel}
            </span>
          )}
          {session.languageCode && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
              {session.languageCode}
            </span>
          )}
          {session.seriesPart > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
              {t("HostSignups.Part", { part: session.seriesPart })}
            </span>
          )}
          {substitute && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
              {t("HostSignups.SubstituteNeeded")}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-700">
          {formatStandardDate(session.startsAt, session.timeZone)}
          {" – "}
          {formatTimeOnly(session.endsAt, session.timeZone)}
          {" · "}
          {t("HostSignups.SignupCount", { count: session.signupCount })}
        </p>
        {session.pairedTitle && (
          <p className="mt-1 text-xs text-slate-700">
            {t("HostSignups.PairedNote", { title: session.pairedTitle })}
          </p>
        )}
        {session.signedUp && (
          <p className="mt-1 text-xs font-medium text-green-700">
            {t("HostSignups.YouSignedUp")}
          </p>
        )}
        {seated && (
          <p className="mt-1 text-xs font-medium text-green-700">
            {t(
              session.myRole === "host"
                ? "HostSignups.YouHost"
                : "HostSignups.YouCohost",
            )}
          </p>
        )}
      </div>
      {seated && session.canStepDown && (
        <WithdrawAsHostButton
          session={{
            sessionId: session.sessionId,
            title: session.title,
          }}
        />
      )}
      {!seated && session.signedUp && (
        <Button
          variant="quaternary"
          size="small"
          disabled={busy}
          onClick={onWithdraw}
          data-testid={`withdraw-${session.sessionId}`}
        >
          {t("HostSignups.Withdraw")}
        </Button>
      )}
      {!seated && !session.signedUp && (
        <Button
          variant="primary"
          size="small"
          disabled={busy}
          onClick={onSignUp}
          data-testid={`signup-${session.sessionId}`}
        >
          {t("HostSignups.SignUp")}
        </Button>
      )}
    </li>
  );
};

export default HostSignupRow;
